import { normalizeClientRouteFamily } from "@/utils/client-route-family";
import type { Event } from "@sentry/nextjs";

export const atlasSentryErrorSources = [
  "client_render",
  "operational_capture",
  "route_handler",
  "server_render",
  "trpc",
  "unknown",
] as const;

export type AtlasSentryErrorSource = (typeof atlasSentryErrorSources)[number];

type SentryEvent = Event;

const httpTransactionPattern = /^(DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT)\s+(\/.*)$/i;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function getHostname(value: unknown): string | null {
  if (!isNonEmptyString(value)) {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "::1" || normalizedValue === "[::1]") {
    return "::1";
  }

  try {
    const url = normalizedValue.includes("://")
      ? new URL(normalizedValue)
      : new URL(`https://${normalizedValue}`);
    const hostname = url.hostname.toLowerCase();
    return hostname.startsWith("[") && hostname.endsWith("]")
      ? hostname.slice(1, -1)
      : hostname;
  } catch {
    return null;
  }
}

function isLocalOrTestHostname(hostname: string): boolean {
  return hostname === "0.0.0.0"
    || hostname === "127.0.0.1"
    || hostname === "::1"
    || hostname === "localhost"
    || hostname.endsWith(".localhost")
    || hostname.endsWith(".test");
}

function isLocalOrTestEvent(event: SentryEvent): boolean {
  const clientContext = event.contexts?.["atlas.client"];
  const clientHost = getHostname(
    clientContext && typeof clientContext === "object" && "clientHost" in clientContext
      ? clientContext.clientHost
      : null,
  );
  const requestHost = getHostname(event.request?.url);
  return Boolean(
    (clientHost && isLocalOrTestHostname(clientHost))
      || (requestHost && isLocalOrTestHostname(requestHost)),
  );
}

function isNextRedirectValue(value: unknown): boolean {
  return isNonEmptyString(value)
    && (value === "NEXT_REDIRECT" || value.startsWith("NEXT_REDIRECT;"));
}

function isExpectedNextRedirect(event: SentryEvent): boolean {
  return isNextRedirectValue(event.message)
    || event.exception?.values?.some(value =>
        isNextRedirectValue(value.type)
        || isNextRedirectValue(value.value)
      ) === true;
}

function getRouteFamilyFromUrl(value: unknown): string | null {
  if (!isNonEmptyString(value)) {
    return null;
  }

  try {
    const url = new URL(value, "https://atlas.invalid");
    return normalizeClientRouteFamily(url.pathname);
  } catch {
    return null;
  }
}

function getTrustedTrpcRouteFamily(value: unknown): string | null {
  if (!isNonEmptyString(value) || value.length > 200) {
    return null;
  }

  const match = value.match(/^\/api\/trpc\/([A-Za-z][\w.-]*)$/);
  return match?.[1] ? `/api/trpc/${match[1]}` : null;
}

export function normalizeSentryTransactionName(value: string): string {
  const httpTransactionMatch = value.match(httpTransactionPattern);
  if (httpTransactionMatch) {
    const method = httpTransactionMatch[1]?.toUpperCase();
    const pathname = httpTransactionMatch[2];
    if (method && pathname) {
      return `${method} ${normalizeClientRouteFamily(pathname)}`;
    }
  }

  if (value.startsWith("/") || /^https?:\/\//i.test(value)) {
    return getRouteFamilyFromUrl(value) ?? value;
  }

  return value;
}

function getErrorSource(event: SentryEvent): AtlasSentryErrorSource {
  const existingSource = event.tags?.["atlas.error_source"];
  if (
    isNonEmptyString(existingSource)
    && atlasSentryErrorSources.includes(existingSource as AtlasSentryErrorSource)
  ) {
    return existingSource as AtlasSentryErrorSource;
  }

  const mechanismType = event.exception?.values?.find(value => value.mechanism?.type)?.mechanism?.type;
  if (mechanismType === "auto.function.nextjs.on_request_error") {
    return "server_render";
  }
  if (mechanismType === "auto.function.nextjs.route_handler") {
    return "route_handler";
  }

  return "unknown";
}

function getCanonicalRouteFamily(event: SentryEvent): string | null {
  const existingRouteFamily = event.tags?.["atlas.route_family"];
  const errorSource = getErrorSource(event);
  const normalizedExistingRouteFamily = errorSource === "trpc"
    ? getTrustedTrpcRouteFamily(existingRouteFamily)
    : isNonEmptyString(existingRouteFamily)
    ? normalizeClientRouteFamily(existingRouteFamily)
    : null;

  // tRPC requests can batch several procedures into one request URL. The
  // procedure-specific route applied at capture time is the stable family.
  if (errorSource === "trpc" && normalizedExistingRouteFamily) {
    return normalizedExistingRouteFamily;
  }

  const requestRouteFamily = getRouteFamilyFromUrl(event.request?.url);
  if (requestRouteFamily) {
    return requestRouteFamily;
  }

  // Next's server-render transaction can contain a filesystem route such as
  // `/(actor)/[organization-slug]/page`. The request boundary supplies the
  // canonical public route tag and it must win over that internal name.
  if (errorSource === "server_render" && normalizedExistingRouteFamily) {
    return normalizedExistingRouteFamily;
  }

  if (isNonEmptyString(event.transaction)) {
    const normalizedTransaction = normalizeSentryTransactionName(event.transaction);
    const transactionRoute = normalizedTransaction.match(httpTransactionPattern)?.[2]
      ?? normalizedTransaction;
    if (transactionRoute.startsWith("/")) {
      return transactionRoute;
    }
  }

  return normalizedExistingRouteFamily;
}

export function normalizeAtlasSentryEvent<TEvent extends SentryEvent>(event: TEvent): null | TEvent {
  if (isLocalOrTestEvent(event) || isExpectedNextRedirect(event)) {
    return null;
  }

  const routeFamily = getCanonicalRouteFamily(event);
  const errorSource = getErrorSource(event);

  if (isNonEmptyString(event.transaction)) {
    const normalizedTransaction = normalizeSentryTransactionName(event.transaction);
    const httpTransactionMatch = normalizedTransaction.match(httpTransactionPattern);
    event.transaction = routeFamily && (errorSource === "server_render" || errorSource === "trpc")
      ? httpTransactionMatch?.[1]
        ? `${httpTransactionMatch[1].toUpperCase()} ${routeFamily}`
        : routeFamily
      : normalizedTransaction;
  }

  event.tags = {
    ...event.tags,
    ...(event.type === "transaction" && errorSource === "unknown"
      ? {}
      : { "atlas.error_source": errorSource }),
    ...(routeFamily
      ? {
        "atlas.route": routeFamily,
        "atlas.route_family": routeFamily,
      }
      : {}),
  };

  return event;
}
